import axios from "axios";
import { getToken } from "./auth";

const API_URL = process.env.REACT_APP_API_BASE_URL;

const axiosInstance = axios.create({
  baseURL: API_URL,
});

axiosInstance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear auth data
      const Cookies = require("js-cookie").default;
      Cookies.remove("auth_token");
      Cookies.remove("user");
      // Redirect to login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

/**
 * Unified response handler
 */
const handleResponse = (res, actionDesc = "Request") => {
  const responseData = res.data || {};

  // If response data is an array, return it directly
  if (Array.isArray(responseData)) {
    return responseData;
  }

  // Otherwise, extract from object structure
  const { success, message, ...rest } = responseData;
  return {
    success,
    message: message || `${actionDesc} completed`,
    ...rest,
  };
};

/**
 * Calculate route optimization
 * @param {string} fromDate - Start date in format YYYY-MM-DD
 * @param {string} toDate - End date in format YYYY-MM-DD
 */
export const calculateRouteOptimization = async (fromDate, toDate) => {
  try {
    const res = await axiosInstance.post(
      "/RouteOptimization/calculate",
      {}, // no body required
      {
        params: { fromDate, toDate },
      },
    );
    return handleResponse(res, "Calculate route optimization");
  } catch (err) {
    console.error("Error calculating route optimization:", err);
    throw err;
  }
};

/**
 * Get all route results
 */
export const getRouteResults = async () => {
  try {
    const res = await axiosInstance.get(
      "/RouteOptimization/getvehicleoptimizations",
    );
    return handleResponse(res, "Get route results");
  } catch (err) {
    console.error("Error fetching route results:", err);
    throw err;
  }
};

/**
 * Assign driver to vehicle optimization
 * @param {string} driverId - The driver ID
 * @param {string} batchId - The vehicle optimization ID
 * @param {number} carId - The car ID
 */
export const assignDriver = async (driverId, batchId, carId) => {
  try {
    const res = await axiosInstance.post(
      "/RouteOptimization/adddrivertovehicle",
      {
        driverId,
        batchId,
        carId,
      },
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return handleResponse(res, "Assign driver");
  } catch (err) {
    console.error("Error assigning driver:", err);
    throw err;
  }
};

/**
 * Update order delivery status
 * @param {Array} orderUpdates - Array of order updates, each containing orderId, vehicleOptimizationId, and isDelivered
 * @example
 * updateOrderDeliveryStatus([
 *   { orderId: "string", vehicleOptimizationId: 0, isDelivered: true }
 * ])
 */
export const updateOrderDeliveryStatus = async (orderUpdates) => {
  try {
    const res = await axiosInstance.post(
      "/RouteOptimization/updateorderdeliverystatus",
      {
        orderUpdates,
      },
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return handleResponse(res, "Update delivery status");
  } catch (err) {
    console.log("Error updating delivery status:", err);
    throw err;
  }
};

/**
 * Get all cities
 */
export const getCities = async () => {
  try {
    const res = await axiosInstance.get("/City");
    return handleResponse(res, "Get cities");
  } catch (err) {
    console.error("Error fetching cities:", err);
    throw err;
  }
};

/**
 * Get a city by ID
 * @param {number} id
 */
export const getCityById = async (id) => {
  try {
    const res = await axiosInstance.get(`/City/${id}`);
    return handleResponse(res, "Get city by ID");
  } catch (err) {
    console.error(`Error fetching city with ID ${id}:`, err);
    throw err;
  }
};

/**
 * Add a new city
 * @param {object} cityDto - City data object
 */
export const addCity = async (cityDto) => {
  try {
    const res = await axiosInstance.post("/City", cityDto, {
      headers: { "Content-Type": "application/json" },
    });
    return handleResponse(res, "Add city");
  } catch (err) {
    console.error("Error adding city:", err);
    throw err;
  }
};

/**
 * Update an existing city
 * @param {number} id
 * @param {object} cityDto - Updated city data
 */
export const updateCity = async (id, cityDto) => {
  try {
    const res = await axiosInstance.put(`/City/${id}`, cityDto, {
      headers: { "Content-Type": "application/json" },
    });
    return handleResponse(res, "Update city");
  } catch (err) {
    console.error(`Error updating city with ID ${id}:`, err);
    throw err;
  }
};

/**
 * Delete a city by ID
 * @param {number} id
 */
export const deleteCity = async (id) => {
  try {
    const res = await axiosInstance.delete(`/City/${id}`);
    return handleResponse(res, "Delete city");
  } catch (err) {
    console.error(`Error deleting city with ID ${id}:`, err);
    throw err;
  }
};

/**
 * Get all vehicles
 */
export const getVehicles = async () => {
  try {
    const res = await axiosInstance.get("/Vehicle/GetVehicles");
    return handleResponse(res, "Get vehicles");
  } catch (err) {
    console.error("Error fetching vehicles:", err);
    throw err;
  }
};

/**
 * Get vehicle by ID
 * @param {number} id
 */
export const getVehicleById = async (id) => {
  try {
    const res = await axiosInstance.get("/Vehicle/GetVehicleById", {
      params: { id },
    });
    return handleResponse(res, "Get vehicle by ID");
  } catch (err) {
    console.error(`Error fetching vehicle with ID ${id}:`, err);
    throw err;
  }
};

/**
 * Add a new vehicle
 * @param {object} vehicleDto - Vehicle data object
 */
export const addVehicle = async (vehicleDto) => {
  try {
    const res = await axiosInstance.post("/Vehicle/AddVehicle", vehicleDto, {
      headers: { "Content-Type": "application/json" },
    });
    return handleResponse(res, "Add vehicle");
  } catch (err) {
    console.error("Error adding vehicle:", err);
    throw err;
  }
};

/**
 * Update an existing vehicle
 * @param {object} vehicleDto - Vehicle data object
 */
export const updateVehicle = async (vehicleDto) => {
  try {
    const res = await axiosInstance.post("/Vehicle/UpdateVehicle", vehicleDto, {
      headers: { "Content-Type": "application/json" },
    });
    return handleResponse(res, "Update vehicle");
  } catch (err) {
    console.error("Error updating vehicle:", err);
    throw err;
  }
};
